import { useAppTranslation } from "@/utils";
import { FiPhone, FiPlus, FiSearch, FiTrash, FileExcel, PhRIdentificationCard } from "@payping/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Input,
  Notification,
  Row,
  Select,
  Typography,
} from "@payping/web-components";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ValidLocalPhoneNumber, ValidNationalId } from "@payping/utils/validation";
import { CreatePlanEmployeeRequest } from "@payping/api/app/api/bnplMerchantTypes";
import { URLS } from "@payping/utilities-shared/consts";

export interface FormInputs {
  phoneNumber: string;
  nationalCode: string;
}
export interface SalaryDeductionType {
  employees?: CreatePlanEmployeeRequest[];
  employeesFile?: Blob;
}
type Props = {
  onNext: (value: SalaryDeductionType) => void;
  onPrev: () => void;
  loading: boolean;
};
const SalaryDeduction: FC<Props> = ({ onNext, onPrev, loading }) => {
  const { t } = useAppTranslation();
  const [notification, contextHolder] = Notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });
  const [form] = Form.useForm<FormInputs>();
  const [editForm] = Form.useForm<FormInputs>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [employees, setEmployees] = useState<CreatePlanEmployeeRequest[]>([]);
  const [showEmployees, setShowEmployees] = useState<CreatePlanEmployeeRequest[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const fileInputSalaryRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>("all");
  const selectOptions = [
    { value: "all", label: t("all") },
    { value: "phoneNumber", label: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.phoneNumber") },
    { value: "nationalCode", label: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.nationalityCode") },
  ];
  const search = useCallback(
    (value: string) => {
      let items: CreatePlanEmployeeRequest[] = employees;
      if (selectedOption === "all") {
        items = employees.filter((a) => a.nationalCode?.includes(value) || a.mobile?.includes(value));
      } else if (selectedOption === "phoneNumber") {
        items = employees.filter((a) => a.mobile?.includes(value));
      } else if (selectedOption === "nationalCode") {
        items = employees.filter((a) => a.nationalCode?.includes(value));
      }
      setShowEmployees(items);
      setSearchText(value);
    },
    [employees, selectedOption],
  );

  useEffect(() => {
    search(searchText);
  }, [employees, selectedOption, searchText, search]);

  const next = () => {
    const data = {} as SalaryDeductionType;
    data.employees = employees;
    data.employeesFile = selectedFile || undefined;
    onNext(data);
  };

  return (
    <Flex vertical gap={"large"} className="tw-w-full">
      {contextHolder}
      <Flex vertical gap={"small"}>
        <Typography.Title level={5} className="!tw-m-0">
          {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.form.title")}
        </Typography.Title>
        <Typography.Text className="!tw-m-0">
          {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.form.description")}
        </Typography.Text>
        <Flex vertical className="tw-w-full tw-max-w-[510px]">
          <Card.Ant className="tw-w-full">
            <Form<FormInputs>
              form={form}
              layout="vertical"
              className="tw-w-full"
              onFieldsChange={() => {
                const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
                setIsFormValid(!hasErrors);
              }}
              onFinish={() => {
                const { nationalCode, phoneNumber } = form.getFieldsValue();
                const data = {} as CreatePlanEmployeeRequest;
                data.nationalCode = nationalCode;
                data.mobile = phoneNumber;
                const index = employees.findIndex((a) => a.nationalCode === nationalCode || a.mobile === phoneNumber);
                if (index === -1) {
                  employees.push(data);
                  setEmployees([...employees]);
                  form.resetFields();
                } else {
                  notification.error({
                    message: String(t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.userAddedBefore")),
                    closable: true,
                    duration: 2,
                  });
                }
              }}
            >
              <Flex gap={"middle"} justify="space-between">
                <Form.Item
                  className="tw-w-full"
                  name={"phoneNumber"}
                  label={`${t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.phoneNumber")}:`}
                  rules={[
                    { required: true },
                    {
                      validator(_, value) {
                        if (!ValidLocalPhoneNumber(value)) {
                          return Promise.reject(
                            new Error(
                              t("common.validations.localPhoneNumber", {
                                name: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.phoneNumber"),
                              }),
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input className="tw-w-full" />
                </Form.Item>

                <Form.Item
                  className="tw-w-full"
                  name={"nationalCode"}
                  label={`${t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.nationalityCode")}:`}
                  rules={[
                    { required: true },
                    {
                      validator(_, value) {
                        if (!ValidNationalId(value)) {
                          return Promise.reject(new Error(t("common.validations.nationalCode")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input className="tw-w-full" />
                </Form.Item>
              </Flex>
            </Form>
            <div>
              <Button
                block={false}
                disabled={!isFormValid}
                className="!tw-px-8"
                loading={false}
                icon={<FiPlus width={14} height={14} />}
                type="default"
                onClick={form.submit}
              >
                {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.add")}
              </Button>
            </div>
          </Card.Ant>
          <Flex vertical gap={"small"} className="tw-mt-5">
            <Typography.Title level={5} className="!tw-m-0">
              {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.addWithExcel")}
            </Typography.Title>
            <Flex gap={"middle"} align={"center"}>
              <Typography.Text className="!tw-m-0">
                {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.excelDescription")}
              </Typography.Text>

              <Link
                rel="noreferrer"
                to={`${URLS.STORAGE_URL}/dashboard-app/bnpl-merchant-salary-plan-sample.xlsx`}
                target="_blank"
              >
                {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.downloadExcel")}
              </Link>
            </Flex>
          </Flex>

          <Card.Ant className="tw-mt-5 tw-w-full tw-max-w-[542px] tw-cursor-pointer tw-border-color-border-secondary tw-bg-color-bg-layout">
            <Flex
              onClick={() => {
                fileInputSalaryRef.current?.click();
              }}
              vertical
              align="center"
            >
              <FileExcel width={33} color="green" />
              <Typography.Text className="!tw-mb-0 tw-mt-5 tw-p-0 tw-font-bold">
                {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.uploadExcelTitle")}
              </Typography.Text>
              <Typography.Text className="!tw-m-0 tw-text-center tw-font-thin tw-text-color-text-description max-sm:!tw-text-xs">
                {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.uploadExcelDescription")}
              </Typography.Text>
              {selectedFile instanceof File ? (
                <Flex gap={"small"} align="center" justify="center">
                  <FiTrash
                    width={16}
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      fileInputSalaryRef.current!.value = "";
                    }}
                  />
                  <Typography.Text type="secondary" className="tw-mt-1">
                    {selectedFile.name}
                  </Typography.Text>
                </Flex>
              ) : null}
            </Flex>
            <input
              ref={fileInputSalaryRef}
              className="!tw-hidden"
              type="file"
              accept=".xlsx"
              multiple={false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { files } = e.target;
                if (files?.length && files[0].name.endsWith(".xlsx")) {
                  const file = new File([files[0]], files[0].name);
                  setSelectedFile(file);
                } else {
                  setSelectedFile(null);
                }
              }}
            />
          </Card.Ant>

          <Flex vertical className="tw-mt-2">
            <Typography.Title level={5} className="!tw-mb-0 tw-mt-5 tw-p-0 tw-font-bold">
              {t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.listUsers")}
            </Typography.Title>
            <Input.Group compact className="tw-mt-4 tw-w-full">
              <Select
                defaultValue="all"
                options={selectOptions}
                className="tw-w-[100px] tw-rounded-l-none tw-bg-color-bg-layout"
                value={selectedOption}
                onChange={(value) => setSelectedOption(value)}
              />
              <Input
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => search(e.target.value)}
                placeholder={t("common.search")}
                className="tw-w-full"
                addonAfter={<FiSearch width={16} />}
                style={{ width: "calc(100% - 100px)" }}
              />
            </Input.Group>
          </Flex>
          {showEmployees.length === 0 ? (
            <Flex
              vertical
              align="center"
              justify="center"
              className="tw-mt-5 tw-h-[336px] tw-w-full tw-rounded-md tw-bg-color-bg-layout tw-p-3"
            >
              <Empty
                className="tw-font-bold"
                description={
                  <span className="tw-text-color-text">
                    {!searchText
                      ? t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.noOneAdded")
                      : t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.noOneFounded")}
                  </span>
                }
              />
            </Flex>
          ) : null}

          <Flex
            vertical
            gap={"small"}
            className="tw-mt-5 tw-h-[336px] tw-w-full tw-overflow-y-auto tw-rounded-md tw-bg-color-bg-layout tw-p-3"
          >
            {showEmployees.map((item, index) => {
              const handleSave = async () => {
                try {
                  const values = await editForm.validateFields();
                  const updatedList = [...showEmployees];
                  updatedList[index] = {
                    ...updatedList[index],
                    mobile: values.phoneNumber,
                    nationalCode: values.nationalCode,
                  };
                  setShowEmployees([...updatedList]);
                  const eIndex = employees.findIndex(
                    (a) => a.mobile === item.mobile && a.nationalCode === item.nationalCode,
                  );
                  if (eIndex > -1) {
                    employees[index].mobile = values.phoneNumber;
                    employees[index].nationalCode = values.nationalCode;
                    setEmployees([...employees]);
                  }
                  setEditingIndex(null);
                } catch (err) {
                  /* empty */
                }
              };

              const handleDelete = () => {
                const eIndex = employees.findIndex(
                  (a) => a.mobile === item.mobile && a.nationalCode === item.nationalCode,
                );
                employees.splice(eIndex, 1);
                const sIndex = showEmployees.findIndex(
                  (a) => a.mobile === item.mobile && a.nationalCode === item.nationalCode,
                );
                showEmployees.splice(sIndex, 1);
                setShowEmployees([...showEmployees]);
                setEmployees([...employees]);
              };

              if (editingIndex === index) {
                return (
                  <Form
                    key={index}
                    form={editForm}
                    layout="vertical"
                    initialValues={{
                      phoneNumber: item.mobile,
                      nationalCode: item.nationalCode,
                    }}
                    onFinish={handleSave}
                    className="tw-rounded-md tw-border tw-border-color-bg-text-hover"
                  >
                    <Flex gap={"middle"}>
                      <Form.Item
                        className="tw-mb-0 tw-p-0"
                        name="phoneNumber"
                        label={""}
                        rules={[
                          { required: true },
                          {
                            validator(_, value) {
                              if (!ValidLocalPhoneNumber(value)) {
                                return Promise.reject(
                                  new Error(
                                    t("common.validations.localPhoneNumber", {
                                      name: t("shop.bnpl.addInstallmentPlan.steps.SalaryDeduction.phoneNumber"),
                                    }),
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input className="tw-w-full" />
                      </Form.Item>

                      <Form.Item
                        name="nationalCode"
                        className="tw-mb-0 tw-p-0"
                        label={""}
                        rules={[
                          { required: true },
                          {
                            validator(_, value) {
                              if (!ValidNationalId(value)) {
                                return Promise.reject(new Error(t("common.validations.nationalCode")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input className="tw-w-full" />
                      </Form.Item>
                      <div className="tw-mr-auto tw-flex tw-justify-end">
                        <Button type="link" onClick={editForm.submit}>
                          {t("common.save")}
                        </Button>
                      </div>
                    </Flex>
                  </Form>
                );
              }

              return (
                <div
                  key={index}
                  className="tw-flex tw-h-[48px] tw-w-full tw-items-center tw-rounded-md tw-border tw-border-solid tw-border-color-bg-text-hover tw-px-4 max-xs:tw-px-2"
                >
                  <Row className="tw-w-full" justify="center" align="middle">
                    <Col xs={9} sm={8}>
                      <div className="tw-flex tw-items-center tw-gap-1">
                        <FiPhone width={16} />
                        <Typography.Text className="tw-font-bold max-sm:!tw-text-xs">{item.mobile}</Typography.Text>
                      </div>
                    </Col>

                    <Col xs={9} sm={8}>
                      <div className="tw-flex tw-items-center tw-gap-1">
                        <PhRIdentificationCard width={16} />
                        <Typography.Text className="tw-font-bold max-sm:!tw-text-xs">
                          {item.nationalCode}
                        </Typography.Text>
                      </div>
                    </Col>

                    <Col xs={3} sm={4}>
                      <div className="tw-flex tw-items-center tw-justify-center">
                        <Button
                          type="link"
                          className="max-sm:!tw-text-xs"
                          onClick={() => {
                            setEditingIndex(index);
                            editForm.setFieldsValue({
                              nationalCode: item.nationalCode,
                              phoneNumber: item.mobile,
                            });
                          }}
                        >
                          {t("common.edit")}
                        </Button>
                      </div>
                    </Col>

                    <Col xs={3} sm={4}>
                      <div className="tw-flex tw-items-center tw-justify-center">
                        <Button type="link" className="max-sm:!tw-text-xs" onClick={handleDelete}>
                          {t("common.delete")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </Flex>
        </Flex>
      </Flex>
      <Flex gap={"small"}>
        <Button block={false} onClick={onPrev} className="!tw-px-8" loading={false} type="default">
          {t("common.previousStep")}
        </Button>
        <Button
          block={false}
          disabled={employees.length === 0 && !selectedFile}
          onClick={next}
          className="!tw-px-8"
          loading={loading}
          type="primary"
        >
          {t("common.saveAndContinue")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default SalaryDeduction;
